import { useState } from 'react';
import ShoppingGuideModal from './ShoppingGuideModal';
import ShippingPolicyModal from './ShippingPolicyModal';
import ReturnPolicyModal from './ReturnPolicyModal';
import FAQModal from './FAQModal';
import TermsModal from './TermsModal';
import PrivacyModal from './PrivacyModal';
import BusinessInfoModal from './BusinessInfoModal';

const LOGO_URL = 'https://public.readdy.ai/ai/img_res/9affe33a-1d73-4c60-9c45-05e39dae9ae2.png';

const footerLinks = [
  {
    title: '제품',
    links: ['ZEO ZEOLITE 지오라이트 파우더', '120g', '350g', '1,000g']
  },
  {
    title: '브랜드',
    links: ['브랜드 스토리', '크로아티아 원산지', 'VEN 특허 기술', '인증 현황']
  },
  {
    title: '고객 지원',
    links: ['구매 안내', '배송 정책', '교환·환불', '자주 묻는 질문']
  }
];

export default function Footer() {
  const [shoppingGuideOpen, setShoppingGuideOpen] = useState(false);
  const [shippingPolicyOpen, setShippingPolicyOpen] = useState(false);
  const [returnPolicyOpen, setReturnPolicyOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showBusinessInfo, setShowBusinessInfo] = useState(false);

  const scrollToSection = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLinkClick = (link: string) => {
    if (link === '구매 안내') {
      setShoppingGuideOpen(true);
    } else if (link === '배송 정책') {
      setShippingPolicyOpen(true);
    } else if (link === '교환·환불') {
      setReturnPolicyOpen(true);
    } else if (link === '자주 묻는 질문') {
      setFaqOpen(true);
    } else if (link === '브랜드 스토리') {
      scrollToSection('#brand');
    } else if (link === '크로아티아 원산지') {
      scrollToSection('#brand-croatia');
    } else if (link === 'VEN 특허 기술') {
      scrollToSection('#brand-ven');
    } else if (link === '인증 현황') {
      scrollToSection('#brand-certification');
    } else if (link === 'ZEO ZEOLITE 지오라이트 파우더') {
      scrollToSection('#products');
    } else if (link === '120g') {
      scrollToSection('#product-1');
    } else if (link === '350g') {
      scrollToSection('#product-2');
    } else if (link === '1,000g') {
      scrollToSection('#product-3');
    }
  };

  return (
    <>
      <footer className="bg-[#1E1B18] text-white px-6 pt-16 pb-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 mb-12">
            <div className="lg:col-span-2">
              <img src={LOGO_URL} alt="위딘 로고" className="h-9 w-auto object-contain mb-4" style={{ filter: 'brightness(0) invert(1)' }} />
              <p className="text-white/50 text-sm leading-relaxed mb-6">
                크로아티아 청정 자연에서 온 프리미엄 지올라이트 웰니스 브랜드.
                자연의 치유력으로 당신의 내면에서부터 변화를 만들어갑니다.
              </p>
              <div className="flex gap-3">
                {['ri-instagram-line', 'ri-youtube-line', 'ri-kakao-line', 'ri-blog-line'].map((icon) => (
                  <button key={icon} className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#8B7355] transition-colors duration-300 cursor-pointer">
                    <i className={`${icon} text-white/60 text-sm`}></i>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:contents gap-8 lg:gap-0">
            {footerLinks.map((col) => (
              <div key={col.title}>
                <p className="text-white/70 text-xs font-medium tracking-wider uppercase mb-4">{col.title}</p>
                <ul className="space-y-2.5">
                  {col.links.map((link) => (
                    <li key={link}>
                      <button
                        onClick={() => handleLinkClick(link)}
                        className="text-white/40 text-sm hover:text-white/80 transition-colors duration-200 cursor-pointer text-left"
                      >
                        {link}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white/30 text-xs text-center sm:text-left">
              © 2025 위딘 (Within). All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-5">
              <button
                onClick={() => setShowTerms(true)}
                className="text-white/30 text-xs hover:text-white/60 transition-colors cursor-pointer whitespace-nowrap"
              >
                이용약관
              </button>
              <button
                onClick={() => setShowPrivacy(true)}
                className="text-white/30 text-xs hover:text-white/60 transition-colors cursor-pointer whitespace-nowrap"
              >
                개인정보처리방침
              </button>
              <button
                onClick={() => setShowBusinessInfo(true)}
                className="text-white/30 text-xs hover:text-white/60 transition-colors cursor-pointer whitespace-nowrap"
              >
                사업자정보
              </button>
            </div>
          </div>

          <div className="mt-4 text-center">
            <p className="text-white/20 text-xs leading-relaxed">
              주식회사 위딘 | 사업자등록번호: 396-86-02897<br className="sm:hidden" />
              <span className="hidden sm:inline"> | </span>인천광역시 연수구 인천타워대로 323, 에이동 31층 에스 216호
            </p>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => scrollToSection('#products')}
              className="inline-flex items-center gap-2 text-[#8B7355] text-xs border border-[#8B7355]/30 px-5 py-2 rounded-full hover:bg-[#8B7355]/10 transition-colors duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-up-line text-sm"></i>
              맨 위로
            </button>
          </div>
        </div>
      </footer>

      <ShoppingGuideModal
        isOpen={shoppingGuideOpen}
        onClose={() => setShoppingGuideOpen(false)}
      />
      <ShippingPolicyModal
        isOpen={shippingPolicyOpen}
        onClose={() => setShippingPolicyOpen(false)}
      />
      <ReturnPolicyModal
        isOpen={returnPolicyOpen}
        onClose={() => setReturnPolicyOpen(false)}
      />
      <FAQModal
        isOpen={faqOpen}
        onClose={() => setFaqOpen(false)}
      />
      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyModal onClose={() => setShowPrivacy(false)} />}
      {showBusinessInfo && <BusinessInfoModal onClose={() => setShowBusinessInfo(false)} />}
    </>
  );
}
